/**
 * Utilidades para manejo de mensajes de error
 *
 * Convierte errores técnicos en mensajes amigables para el usuario.
 */

/**
 * Error codes and their corresponding user-friendly messages
 */
export const ERROR_CODES = {
  // Network errors
  NETWORK_ERROR: 'NETWORK_ERROR',
  TIMEOUT_ERROR: 'TIMEOUT_ERROR',
  
  // Authentication errors
  TOKEN_EXPIRED: 'TOKEN_EXPIRED',
  INVALID_TOKEN: 'INVALID_TOKEN',
  UNAUTHORIZED: 'UNAUTHORIZED',
  
  // Validation errors
  INVALID_INPUT: 'INVALID_INPUT',
  MISSING_REQUIRED: 'MISSING_REQUIRED',

  // Permission errors
  INSUFFICIENT_PERMISSIONS: 'INSUFFICIENT_PERMISSIONS',
  ADMIN_REQUIRED: 'ADMIN_REQUIRED',
  
  // Server errors
  SERVER_ERROR: 'SERVER_ERROR',
  DATABASE_ERROR: 'DATABASE_ERROR',

  // Rate limiting
  RATE_LIMIT_EXCEEDED: 'RATE_LIMIT_EXCEEDED',

  // Generic
  UNKNOWN_ERROR: 'UNKNOWN_ERROR'
};

/**
 * User-friendly error messages in Spanish
 */
export const ERROR_MESSAGES = {
  [ERROR_CODES.NETWORK_ERROR]: 'Error de conexión. Verifica tu conexión a internet.',
  [ERROR_CODES.TIMEOUT_ERROR]: 'La operación tardó demasiado tiempo. Intenta de nuevo.',
  
  [ERROR_CODES.TOKEN_EXPIRED]: 'Tu sesión ha expirado. Por favor, inicia sesión nuevamente.',
  [ERROR_CODES.INVALID_TOKEN]: 'Token de autenticación inválido. Por favor, inicia sesión nuevamente.',
  [ERROR_CODES.UNAUTHORIZED]: 'No tienes permisos para realizar esta acción.',
  
  [ERROR_CODES.INVALID_INPUT]: 'Los datos ingresados no son válidos. Verifica la información.',
  [ERROR_CODES.MISSING_REQUIRED]: 'Faltan campos obligatorios. Completa toda la información requerida.',

  [ERROR_CODES.INSUFFICIENT_PERMISSIONS]: 'No tienes suficientes permisos para realizar esta acción.',
  [ERROR_CODES.ADMIN_REQUIRED]: 'Se requieren permisos de administrador para esta operación.',
  
  [ERROR_CODES.SERVER_ERROR]: 'Error interno del servidor. Intenta de nuevo más tarde.',
  [ERROR_CODES.DATABASE_ERROR]: 'Error de base de datos. Contacta al soporte técnico.',

  [ERROR_CODES.RATE_LIMIT_EXCEEDED]: 'Has realizado demasiadas solicitudes. Espera unos minutos.',

  [ERROR_CODES.UNKNOWN_ERROR]: 'Ha ocurrido un error inesperado. Intenta de nuevo.'
};

/**
 * Parse API error and return appropriate error code and message
 * @param {Error|string} error - The error object or message
 * @returns {Object} - { code, message, isRetryable }
 */
export const parseError = (error) => {
  let errorString = '';
  let originalMessage = '';
  
  if (typeof error === 'string') {
    errorString = error.toLowerCase();
    originalMessage = error;
  } else if (error?.message) {
    errorString = error.message.toLowerCase();
    originalMessage = error.message;
  } else {
    return {
      code: ERROR_CODES.UNKNOWN_ERROR,
      message: ERROR_MESSAGES[ERROR_CODES.UNKNOWN_ERROR],
      isRetryable: true
    };
  }
  
  // Network errors
  if (errorString.includes('network_error') || errorString.includes('network') || errorString.includes('fetch')) {
    return {
      code: ERROR_CODES.NETWORK_ERROR,
      message: ERROR_MESSAGES[ERROR_CODES.NETWORK_ERROR],
      isRetryable: true
    };
  }
  
  if (errorString.includes('timeout') || errorString.includes('tiempo')) {
    return {
      code: ERROR_CODES.TIMEOUT_ERROR,
      message: ERROR_MESSAGES[ERROR_CODES.TIMEOUT_ERROR],
      isRetryable: true
    };
  }
  
  // Validation errors específicos
  if ((errorString.includes('nombre:') || errorString.includes('apellido:') || errorString.includes('teléfono:') || 
       errorString.includes('contraseña:') || errorString.includes('email:')) && originalMessage) {
    // Estos son mensajes de validación específicos del backend, úsalos directamente
    return {
      code: ERROR_CODES.INVALID_INPUT,
      message: originalMessage,
      isRetryable: false
    };
  }
  
  // Campos requeridos
  if ((errorString.includes('requerido') || errorString.includes('required') || errorString.includes('obligatorio')) && originalMessage) {
    return {
      code: ERROR_CODES.MISSING_REQUIRED,
      message: originalMessage,
      isRetryable: false
    };
  }
  
  // Formato inválido
  if ((errorString.includes('formato') && errorString.includes('inválido')) || 
      (errorString.includes('format') && errorString.includes('invalid'))) {
    return {
      code: ERROR_CODES.INVALID_INPUT,
      message: originalMessage || 'Formato de datos inválido',
      isRetryable: false
    };
  }
  
  // Authentication errors
  if (errorString.includes('token') && (errorString.includes('expired') || errorString.includes('expirado'))) {
    return {
      code: ERROR_CODES.TOKEN_EXPIRED,
      message: ERROR_MESSAGES[ERROR_CODES.TOKEN_EXPIRED],
      isRetryable: false
    };
  }
  
  if (errorString.includes('token') && errorString.includes('invalid')) {
    return {
      code: ERROR_CODES.INVALID_TOKEN,
      message: ERROR_MESSAGES[ERROR_CODES.INVALID_TOKEN],
      isRetryable: false
    };
  }
  
  if (errorString.includes('unauthorized') || errorString.includes('no autorizado')) {
    return {
      code: ERROR_CODES.UNAUTHORIZED,
      message: ERROR_MESSAGES[ERROR_CODES.UNAUTHORIZED],
      isRetryable: false
    };
  }

  // Registro de usuarios - email ya existe
  if ((errorString.includes('email') || errorString.includes('correo')) &&
      (errorString.includes('ya está registrado') || errorString.includes('ya existe') || errorString.includes('already exists'))) {
    return {
      code: ERROR_CODES.INVALID_INPUT,
      message: originalMessage || 'El email ya está registrado. Intenta con otro email o inicia sesión.',
      isRetryable: false
    };
  }

  // Rate limiting
  if (errorString.includes('demasiadas solicitudes') ||
      errorString.includes('demasiados intentos') ||
      errorString.includes('demasiados registros') ||
      errorString.includes('demasiados cambios') ||
      errorString.includes('demasiados reenvíos') ||
      errorString.includes('too many requests')) {
    return {
      code: ERROR_CODES.RATE_LIMIT_EXCEEDED,
      message: originalMessage || ERROR_MESSAGES[ERROR_CODES.RATE_LIMIT_EXCEEDED],
      isRetryable: true
    };
  }

  // Permission errors
  if (errorString.includes('admin') && (errorString.includes('required') || errorString.includes('requerido'))) {
    return {
      code: ERROR_CODES.ADMIN_REQUIRED,
      message: ERROR_MESSAGES[ERROR_CODES.ADMIN_REQUIRED],
      isRetryable: false
    };
  }
  
  if (errorString.includes('permissions') || errorString.includes('permisos')) {
    return {
      code: ERROR_CODES.INSUFFICIENT_PERMISSIONS,
      message: ERROR_MESSAGES[ERROR_CODES.INSUFFICIENT_PERMISSIONS],
      isRetryable: false
    };
  }
  
  // Server errors
  if (errorString.includes('server') || errorString.includes('servidor')) {
    return {
      code: ERROR_CODES.SERVER_ERROR,
      message: ERROR_MESSAGES[ERROR_CODES.SERVER_ERROR],
      isRetryable: true
    };
  }
  
  if (errorString.includes('database') || errorString.includes('base de datos')) {
    return {
      code: ERROR_CODES.DATABASE_ERROR,
      message: ERROR_MESSAGES[ERROR_CODES.DATABASE_ERROR],
      isRetryable: false
    };
  }
  
  // Si el mensaje del backend es específico y descriptivo, úsalo directamente
  // Esto preserva mensajes como "El email ya está registrado", "Nombre: debe tener al menos 2 caracteres", etc.
  // Pero excluye mensajes genéricos como "Error", "Failed", etc.
  if (originalMessage && 
      originalMessage.length > 5 && 
      originalMessage.length < 200 &&
      !errorString.match(/^(error|failed|exception|undefined|null)$/)) {
    return {
      code: ERROR_CODES.UNKNOWN_ERROR,
      message: originalMessage,
      isRetryable: false
    };
  }
  
  // Default genérico solo para errores verdaderamente desconocidos
  return {
    code: ERROR_CODES.UNKNOWN_ERROR,
    message: ERROR_MESSAGES[ERROR_CODES.UNKNOWN_ERROR],
    isRetryable: true
  };
};

/**
 * Get user-friendly error message from error
 * @param {Error|string} error - The error
 * @returns {string} - User-friendly error message
 */
export const getErrorMessage = (error) => {
  return parseError(error).message;
};

/**
 * Check if error is retryable
 * @param {Error|string} error - The error
 * @returns {boolean} - True if retryable
 */
export const isErrorRetryable = (error) => {
  return parseError(error).isRetryable;
};